import { useEffect, useRef, useState } from 'react';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import useUserStore from '../stores/useUserStore';
import AirspaceQueueList from './AirspaceQueueList';
import AirspaceEventDetail from './AirspaceEventDetail';
import { DOCK_MOTION } from '../components/ChatBot/flerpieDock';
import PageLayout from '../components/ui/PageLayout';
import { useViewportFill } from '../components/ui/viewport';
import { IconButton, buttonClass } from '../components/ui/Button';
import Tooltip from '../components/ui/Tooltip';
import { IconClose } from '../components/ui/icons';
import { Panel } from '../components/ui/Panel';
import EmptyState from '../components/ui/EmptyState';

/**
 * /pilots/airspace — airspace authorization workspace.
 *
 * Layout (lg+):
 *   ┌────────┬───────────────────────────────────────────────────┐
 *   │ Queue  │  Header                                           │
 *   │ rail   ├───────────────────────────────────────────────────│
 *   │ (own   │  MAP (full width of content area)                 │
 *   │  scroll├───────────────────────────────────────────────────│
 *   │ )      │  Workflow content │ Nearby History │ Messages     │
 *   │        │  (~50%)           │   (~25%)       │  (~25%)      │
 *   └────────┴───────────────────────────────────────────────────┘
 *
 * Layout (below lg):
 *   Queue rail collapses entirely. A "Queue" button in the page header opens
 *   it as an overlay drawer that slides in from the left — content underneath
 *   is NOT pushed; the drawer floats over with a backdrop.
 *
 * Scrolling: the outer card fills the window below its own top (ui/viewport useViewportFill) so
 * the queue can scroll inside its own column without the workflow stretching
 * the page taller. Each column (queue, content) scrolls independently.
 */
const PilotsPage: React.FC = () => {
  const user = useUserStore(s => s.user);
  const location = useLocation();
  const history = useHistory();

  const params = new URLSearchParams(location.search);
  const initialEventId = params.get('event');
  const initialStatus = params.get('status') || null;

  const [selectedEventId, setSelectedEventId] = useState<string | null>(initialEventId);
  const [statusFilter, setStatusFilter] = useState<string | null>(initialStatus);
  const [queueVersion, setQueueVersion] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const workbench = useRef<HTMLDivElement>(null);
  useViewportFill(workbench, { mode: 'height', min: 480 });
  const bumpQueueVersion = () => setQueueVersion(v => v + 1);

  // state → URL sync (so the URL reflects current selection / filter)
  useEffect(() => {
    const next = new URLSearchParams();
    if (selectedEventId) next.set('event', selectedEventId);
    if (statusFilter) next.set('status', statusFilter);
    const search = next.toString();
    if (search !== location.search.replace(/^\?/, '')) {
      history.replace({ pathname: '/pilots/airspace', search: search ? `?${search}` : '' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEventId, statusFilter]);

  // URL → state sync (so browser back/forward and external deep-links work)
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const urlEvent = p.get('event');
    const urlStatus = p.get('status');
    if (urlEvent !== selectedEventId) setSelectedEventId(urlEvent);
    if ((urlStatus || null) !== statusFilter) setStatusFilter(urlStatus || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Close the drawer on selection / ESC so it doesn't linger after a pick
  useEffect(() => { if (drawerOpen) setDrawerOpen(false); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedEventId]);
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  if (!user || !(user.can_pilot || user.can_admin || user.isadmin)) {
    return <Redirect to="/" />;
  }

  const queueList = (
    <AirspaceQueueList
      statusFilter={statusFilter}
      onStatusFilterChange={setStatusFilter}
      selectedId={selectedEventId}
      onSelect={setSelectedEventId}
      version={queueVersion}
    />
  );

  return (
    <PageLayout width="full" kind="workspace" className="pt-4 space-y-4">
      <div className="flex justify-between items-center gap-3">
        <div className="flex items-baseline gap-3">
          {/* Drawer trigger — visible only when queue is collapsed (below lg) */}
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className={`${buttonClass('tonal', 'sm')} lg:hidden`}
            aria-label="Open queue"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            Queue
          </button>
          <h1 className="text-2xl font-bold text-primary">Pilots</h1>
          <span className="text-xs uppercase tracking-widest text-on-surface-variant hidden sm:inline">Airspace Authorizations</span>
        </div>
      </div>

      <Panel padding="none" label="Airspace authorizations" className="overflow-hidden">
        <div ref={workbench} className="flex">
          {/* Queue rail — fixed-width on lg+, individually scrollable */}
          <aside className="hidden lg:flex flex-col w-72 flex-shrink-0 border-r border-gray-700/60 bg-gray-900/40 overflow-hidden">
            {queueList}
          </aside>

          {/* Content area — independently scrollable */}
          <main className="flex-1 min-w-0 overflow-y-auto">
            {selectedEventId ? (
              <AirspaceEventDetail
                eventId={selectedEventId}
                onClose={() => setSelectedEventId(null)}
                onMutated={bumpQueueVersion}
                onSelectEvent={setSelectedEventId}
              />
            ) : (
              <EmptyState variant="area" className="h-full justify-center">
                Select an event from the queue to begin review.
              </EmptyState>
            )}
          </main>
        </div>
      </Panel>

      {/* Off-canvas queue drawer — overlay, doesn't push page content.
          z-values sit above leaflet's pane stack (max z-700) so the map
          inside the main panel can't bleed through the drawer / backdrop. */}
      {drawerOpen && (
        <>
          <div
            className={`fixed inset-y-0 left-[var(--flerpie-dock,0px)] right-0 transition-[left] ${DOCK_MOTION} z-[1200] bg-black/50 backdrop-blur-[1px] lg:hidden`}
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            className={`fixed inset-y-0 left-[var(--flerpie-dock,0px)] transition-[left] ${DOCK_MOTION} z-[1201] w-72 max-w-[85vw] bg-gray-900 border-r border-gray-700/60 shadow-2xl flex flex-col lg:hidden`}
            role="dialog"
            aria-label="Queue"
          >
            <div className="px-3 py-2 flex items-center justify-between border-b border-gray-700/60 flex-shrink-0">
              <span className="text-[11px] uppercase tracking-widest text-on-surface-variant font-semibold">Queue</span>
              <Tooltip label="Close the queue">
                <IconButton label="Close queue" size="sm" onClick={() => setDrawerOpen(false)}>
                  <IconClose />
                </IconButton>
              </Tooltip>
            </div>
            <div className="flex-1 overflow-hidden flex flex-col min-h-0">
              {queueList}
            </div>
          </aside>
        </>
      )}
    </PageLayout>
  );
};

export default PilotsPage;
